import type { PayloadAction} from '@reduxjs/toolkit';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { useState } from 'react';
import { array } from 'ts-pattern/dist/patterns';

import { useAppDispatch, useAppSelector } from '@shared/hooks';
import type { ColorTag } from '@shared/types/common';

const colorTagAdapter = createEntityAdapter({
	selectId: (colorTag: ColorTag) => colorTag.id,
});

const colorTagSlice = createSlice({
	name: 'common/colorTags',
	initialState: colorTagAdapter.getInitialState({
		selectedColorTag: {} as ColorTag | null,
	}),
	reducers: {
		colorTagAdd: colorTagAdapter.addOne,
		colorTagsAdd: colorTagAdapter.addMany,
		colorTagsRemove: colorTagAdapter.removeAll,
		colorTagRemove: colorTagAdapter.removeOne,
		colorTagsSet: colorTagAdapter.setAll,
		colorTagSet: colorTagAdapter.setOne,
		colorTagUpdate: colorTagAdapter.updateOne,
		colorTagsReceived(state, action) {
			colorTagAdapter.setAll(state, action.payload.common.colorTags);
		},
		selectedColorTagSet: (state, action: PayloadAction<ColorTag | null>) => {
			state.selectedColorTag = action.payload;
		},
		resetSelectedColorTag: (state) => {
			state.selectedColorTag = {} as ColorTag;
		},
	},
});

export const colorTagReducer = colorTagSlice.reducer;
export const colorTagActions = colorTagSlice.actions;

const colorTagSelectors = colorTagAdapter.getSelectors();

export const useColorTag = (id: number) => {
	const dispatch = useAppDispatch();

	const colorTag = useAppSelector((state) =>
		colorTagSelectors.selectById(state.common.colorTags, id)
	);

	const addColorTags = (colorTags: ColorTag[]) => dispatch(colorTagActions.colorTagsAdd(colorTags));

	const ids = useAppSelector((state) => colorTagSelectors.selectIds(state.common.colorTags));

	const updateColorTag = (id: number, changes: Partial<ColorTag>) =>
		dispatch(colorTagActions.colorTagUpdate({ id: id, changes: changes }));

	const setColorTag = (colorTag: ColorTag) => dispatch(colorTagActions.colorTagSet(colorTag));

	const removeColorTag = (id: number) => dispatch(colorTagActions.colorTagRemove(id));

	return {
		colorTag,

		addColorTags,
		setColorTag,
		removeColorTag,
		updateColorTag,
		ids,
	};
};

export const useColorTags = () => {
	const dispatch = useAppDispatch();

	const addColorTag = (colorTag: ColorTag) => dispatch(colorTagActions.colorTagAdd(colorTag));

	const colorTags = useAppSelector((state) => colorTagSelectors.selectAll(state.common.colorTags));
	const selectedColorTag = useAppSelector((state) => state.common.colorTags.selectedColorTag);

	const setColorTags = (colorTags: ColorTag[]) => dispatch(colorTagActions.colorTagsSet(colorTags));
	const setSelectedColorTag = (colorTag: ColorTag | null) => {
		dispatch(colorTagActions.selectedColorTagSet(colorTag));
	};

	const initialColorTag = (id: string) => {
		const colorTag = colorTags.find((colorTag: ColorTag) => {
			if (colorTag.id.toString() === id) return colorTag;
		});
		colorTag && dispatch(colorTagActions.selectedColorTagSet(colorTag));
	};

	const resetSelectedColorTag = () => dispatch(colorTagActions.resetSelectedColorTag());

	const onSelect = (colorTag: ColorTag) => {
		setSelectedColorTag(colorTag);
	};

	const isSelected = (colorTag: ColorTag | null) => {
		return selectedColorTag && colorTag ? selectedColorTag.id === colorTag.id : false;
	};

	return {
		initialColorTag,
		colorTags,
		selectedColorTag,
		setColorTags,
		addColorTag,
		setSelectedColorTag,
		onSelect,
		isSelected,
		resetSelectedColorTag,
	};
};
